'use client';

import { runningClientSide } from '@benzinga/utils';

export interface TaboolaItemInfo {
  branding: string;
  destinationUrl: string;
  element: HTMLElement;
  isBenzingaPartnerContent: boolean;
  isSyndicated: boolean;
  itemId: string;
  thumbnailUrl: string;
  title: string;
}

export interface TaboolaSlotRenderEvent {
  containerId: string;
  isEmpty: boolean;
  items: TaboolaItemInfo[];
}

type TaboolaRenderCallback = (event: TaboolaSlotRenderEvent) => void;

const BENZINGA_BRANDINGS = ['benzinga', 'benzinga partners'];

class TaboolaAdManager {
  private containerCallbacks: Map<string, Set<TaboolaRenderCallback>> = new Map();
  private containerElements: Map<string, HTMLElement> = new Map();
  private discoveredItems: Map<string, TaboolaItemInfo> = new Map();
  private observedContainers: Map<string, MutationObserver> = new Map();
  private renderListenerAttached = false;

  isBenzingaBranding(branding: string): boolean {
    const normalized = branding.toLowerCase().trim();
    return BENZINGA_BRANDINGS.some(b => normalized === b || normalized.startsWith(b));
  }

  extractBrandingText(itemElement: Element): string {
    const brandingAttr = itemElement.getAttribute('data-item-branding');
    if (brandingAttr) return brandingAttr.trim();

    const brandingInner = itemElement.querySelector('.branding-inner');
    if (brandingInner) {
      return brandingInner.textContent?.trim() || '';
    }

    const inlineBranding = itemElement.querySelector('.inline-branding');
    if (inlineBranding) {
      return inlineBranding.textContent?.trim() || '';
    }

    const genericBranding = itemElement.querySelector(
      '[class*="branding"]:not(.composite-branding):not(.branding-separator)',
    );
    if (genericBranding) {
      const firstTextNode = Array.from(genericBranding.childNodes).find(
        node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
      );
      if (firstTextNode) {
        return firstTextNode.textContent?.trim() || '';
      }
      return genericBranding.textContent?.trim().split(/\s{2,}/)[0] || '';
    }

    return '';
  }

  extractDestinationUrl(itemElement: Element): string {
    const urlAttr = itemElement.getAttribute('data-item-url');
    if (urlAttr) return urlAttr;

    const anchor = itemElement.querySelector('a[href]') as HTMLAnchorElement;
    if (anchor?.href) {
      return anchor.href;
    }

    return '';
  }

  extractItemInfo(itemElement: Element): TaboolaItemInfo | null {
    const itemId = itemElement.getAttribute('data-item-id');
    if (!itemId) return null;

    const title = itemElement.getAttribute('data-item-title') || '';
    const thumbnailUrl = itemElement.getAttribute('data-item-thumb') || '';
    const isSyndicated = itemElement.getAttribute('data-item-syndicated') === 'true';
    const branding = this.extractBrandingText(itemElement);
    const destinationUrl = this.extractDestinationUrl(itemElement);

    const isBenzingaUrl = destinationUrl.includes('benzinga.com');
    const isBenzingaPartnerContent = isSyndicated && (this.isBenzingaBranding(branding) || isBenzingaUrl);

    return {
      branding,
      destinationUrl,
      element: itemElement as HTMLElement,
      isBenzingaPartnerContent,
      isSyndicated,
      itemId,
      thumbnailUrl,
      title,
    };
  }

  isElementVisible(element: Element): boolean {
    const htmlElement = element as HTMLElement;
    if (htmlElement.offsetParent === null && htmlElement.style.position !== 'fixed') {
      return false;
    }
    const style = window.getComputedStyle(htmlElement);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    const rect = htmlElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    const isCarousel = htmlElement.classList.contains('tbl-recommendation-reel');
    if (isCarousel) {
      return false;
    }

    return true;
  }

  extractBenzingaUnits(container: Element): TaboolaItemInfo[] {
    const items = container.querySelectorAll('[data-item-id]');
    const benzingaUnits: TaboolaItemInfo[] = [];

    items.forEach(item => {
      if (!this.isElementVisible(item)) return;

      const itemInfo = this.extractItemInfo(item);
      if (itemInfo && itemInfo.isBenzingaPartnerContent) {
        if (!this.discoveredItems.has(itemInfo.itemId)) {
          this.discoveredItems.set(itemInfo.itemId, itemInfo);
        }
        benzingaUnits.push(itemInfo);
      }
    });

    return benzingaUnits;
  }

  observeContainer(container: HTMLElement, callback: TaboolaRenderCallback): () => void {
    if (!runningClientSide()) {
      return () => {
        /* no-op for SSR */
      };
    }

    const containerId = container.id || `taboola-container-${Date.now()}`;

    if (!this.containerCallbacks.has(containerId)) {
      this.containerCallbacks.set(containerId, new Set());
    }
    this.containerCallbacks.get(containerId)?.add(callback);
    this.containerElements.set(containerId, container);

    this.attachRenderListener();
    this.setupMutationObserverForElement(containerId, container);
    this.scanContainerElement(containerId, container);

    return () => {
      const callbacks = this.containerCallbacks.get(containerId);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.containerCallbacks.delete(containerId);
          this.containerElements.delete(containerId);

          const observer = this.observedContainers.get(containerId);
          if (observer) {
            observer.disconnect();
            this.observedContainers.delete(containerId);
          }
        }
      }
    };
  }

  getDiscoveredItems(): Map<string, TaboolaItemInfo> {
    return this.discoveredItems;
  }

  getItemById(itemId: string): TaboolaItemInfo | undefined {
    return this.discoveredItems.get(itemId);
  }

  private scanContainerElement(containerId: string, container: HTMLElement): void {
    const benzingaUnits = this.extractBenzingaUnits(container);

    const callbacks = this.containerCallbacks.get(containerId);
    if (callbacks && callbacks.size > 0) {
      const event: TaboolaSlotRenderEvent = {
        containerId,
        isEmpty: benzingaUnits.length === 0,
        items: benzingaUnits,
      };

      callbacks.forEach(callback => {
        try {
          callback(event);
        } catch (err) {
          console.error('[TaboolaAdManager] Callback error:', err);
        }
      });
    }
  }

  private setupMutationObserverForElement(containerId: string, container: HTMLElement): void {
    if (this.observedContainers.has(containerId)) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastVisibleItemCount = 0;

    const checkForNewItems = () => {
      const visibleItems = Array.from(container.querySelectorAll('[data-item-id]')).filter(el =>
        this.isElementVisible(el),
      );
      if (visibleItems.length !== lastVisibleItemCount) {
        lastVisibleItemCount = visibleItems.length;
        this.scanContainerElement(containerId, container);
      }
    };

    const observer = new MutationObserver(() => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(checkForNewItems, 50);
    });

    observer.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    this.observedContainers.set(containerId, observer);
  }

  private attachRenderListener(): void {
    if (this.renderListenerAttached) return;
    if (!runningClientSide()) return;

    if (!window['_taboola']) {
      window['_taboola'] = [];
    }

    this.renderListenerAttached = true;

    window['_taboola'].push({
      handler: () => {
        this.containerElements.forEach((container, containerId) => {
          this.scanContainerElement(containerId, container);
        });
      },
      listenTo: 'render',
    });
  }
}

export const taboolaAdManager = new TaboolaAdManager();
